import { logger } from './logger';
import { LogCategory } from './types';
import type { OutboxRow } from './types';
import { telemetryEnabled } from './runtimeContext';
import {
    beaconRows,
    claimBatch,
    envelope,
    recoverStranded,
    release,
    selectWithin,
    settle,
} from './outbox';

export const TELEMETRY_URL = 'api/v3/telemetry';

// The anonymous caps: the client cannot know how the server will attribute it.
export const MAX_BATCH_RECORDS = 20;
export const MAX_BATCH_BYTES = 30_000;

export const BASE_BACKOFF_MS = 30_000;
export const MAX_BACKOFF_MS = 30 * 60 * 1000;
export const REQUEST_TIMEOUT_MS = 30_000;
export const DRAIN_INTERVAL_MS = 5 * 60 * 1000;

type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

export interface TelemetrySyncDeps {
    fetch?: FetchLike;
    sendBeacon?: (url: string, data: Blob) => boolean;
    now?: () => number;
    random?: () => number;
}

interface PostOutcome {
    status: number;
    body?: unknown;
    retryAfter?: string | null;
}

function asObject(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

// Index before id: only the index always identifies one row.
export function disposedIds(batch: OutboxRow[], body: unknown): Set<number> {
    const response = asObject(body);
    const byUuid = new Map(batch.map((row) => [row.uuid, row.id as number]));
    const ids = new Set<number>();

    for (const uuid of [...asArray(response.accepted), ...asArray(response.dropped)]) {
        if (typeof uuid === 'string' && byUuid.has(uuid)) {
            ids.add(byUuid.get(uuid) as number);
        }
    }

    for (const entry of asArray(response.rejected)) {
        const { index, id } = asObject(entry);

        if (Number.isInteger(index) && batch[index as number]) {
            ids.add(batch[index as number].id as number);
        }
        else if (typeof id === 'string' && byUuid.has(id)) {
            ids.add(byUuid.get(id) as number);
        }
    }

    return ids;
}

export function retryAfterMs(header: string | null | undefined, now: number): number {
    if (!header) {
        return 0;
    }

    const seconds = Number(header);

    if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000);
    }

    const date = Date.parse(header);

    return Number.isNaN(date) ? 0 : Math.max(0, date - now);
}

export class TelemetrySync {
    private deps: TelemetrySyncDeps;
    private current: Promise<void> | undefined;
    private rerun = false;
    private halted = false;
    private stopped = false;
    private failures = 0;
    private nextAttemptAt = 0;
    private maxRecords = MAX_BATCH_RECORDS;
    private splitRecords: number | undefined;
    private interval: ReturnType<typeof setInterval> | undefined;
    private retryTimer: ReturnType<typeof setTimeout> | undefined;
    private beaconed = new Set<string>();

    constructor(deps: TelemetrySyncDeps = {}) {
        this.deps = deps;
    }

    private now(): number {
        return (this.deps.now ?? Date.now)();
    }

    get isHalted(): boolean {
        return this.halted;
    }

    get retryAt(): number {
        return this.nextAttemptAt;
    }

    get batchLimit(): number {
        return Math.min(this.maxRecords, this.splitRecords ?? this.maxRecords);
    }

    start(): void {
        if (this.interval !== undefined || !telemetryEnabled()) {
            return;
        }

        this.stopped = false;

        this.interval = setInterval(() => {
            this.requestDrain('interval').then();
        }, DRAIN_INTERVAL_MS);
        this.requestDrain('startup').then();
    }

    stop(): void {
        this.stopped = true;
        clearInterval(this.interval);
        clearTimeout(this.retryTimer);
        this.interval = undefined;
        this.retryTimer = undefined;
    }

    requestDrain(trigger: string): Promise<void> {
        if (this.current) {
            this.rerun = true;
            return this.current;
        }

        this.current = this.run(trigger).finally(() => {
            this.current = undefined;
        });

        return this.current;
    }

    private canSend(): boolean {
        return telemetryEnabled()
            && !this.halted
            && globalThis.navigator?.onLine !== false
            && this.now() >= this.nextAttemptAt;
    }

    private async run(trigger: string): Promise<void> {
        try {
            do {
                this.rerun = false;
                await this.drain(trigger);
            } while (this.rerun);
        }
        catch (e: unknown) {
            // DEBUG never ships, so a failing drain cannot feed its own queue.
            logger.debug(
                LogCategory.SYNC,
                'sync.telemetry.failed',
                { trigger, type: (e as Error)?.name },
                'Telemetry drain failed',
            ).then();
        }
    }

    private async drain(trigger: string): Promise<void> {
        if (!this.canSend()) {
            return;
        }

        await recoverStranded(this.now());

        while (!this.stopped) {
            const claimedAt = this.now();
            const batch = await claimBatch(this.batchLimit, MAX_BATCH_BYTES, claimedAt);

            if (batch.length === 0) {
                return;
            }

            const outcome = await this.post(batch);
            const proceed = await this.dispose(batch, claimedAt, outcome);

            if (outcome.status !== 200) {
                logger.debug(
                    LogCategory.SYNC,
                    'sync.telemetry.deferred',
                    { trigger, status: outcome.status },
                    'Telemetry batch not delivered',
                ).then();
            }

            if (!proceed) {
                return;
            }
        }
    }

    private async post(batch: OutboxRow[]): Promise<PostOutcome> {
        const send = this.deps.fetch ?? ((url, init) => globalThis.fetch(url, init));
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        try {
            const response = await send(TELEMETRY_URL, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: envelope(batch),
                credentials: 'same-origin',
                signal: controller.signal,
            });
            let body: unknown;

            try {
                body = await response.json();
            }
            catch {
                body = undefined;
            }

            return {
                status: response.status,
                body,
                retryAfter: response.headers?.get('Retry-After'),
            };
        }
        catch {
            return { status: 0 };
        }
        finally {
            clearTimeout(timeout);
        }
    }

    private async dispose(
        batch: OutboxRow[],
        claimedAt: number,
        { status, body, retryAfter }: PostOutcome,
    ): Promise<boolean> {
        const ids = batch.map((row) => row.id as number);
        const response = asObject(body);

        if (status === 200) {
            const disposed = disposedIds(batch, body);
            const unmentioned = ids.filter((id) => !disposed.has(id));

            await settle([...disposed]);

            // A captive portal answers 200 too.
            if (unmentioned.length > 0) {
                await release(unmentioned, claimedAt, true);
                this.backOff(0);
                return false;
            }

            this.failures = 0;
            this.nextAttemptAt = 0;
            this.splitRecords = undefined;
            return true;
        }

        const maxRecords = response.maxRecords as number;

        if (status === 400 && response.error === 'batch_too_large'
            && Number.isInteger(maxRecords) && maxRecords > 0 && maxRecords < batch.length) {
            this.maxRecords = maxRecords;
            await release(ids, claimedAt, false);
            return true;
        }

        // About this build, not the records.
        const unsupported = status === 400 && response.error === 'unsupported_envelope_version';

        if (status === 501 || unsupported) {
            await release(ids, claimedAt, false);
            this.halted = true;
            return false;
        }

        if (status === 413) {
            if (batch.length > 1) {
                this.splitRecords = Math.max(1, Math.floor(batch.length / 2));
                await release(ids, claimedAt, false);
                return true;
            }

            await settle(ids);
            this.splitRecords = undefined;
            return true;
        }

        if (status === 0 && globalThis.navigator?.onLine === false) {
            await release(ids, claimedAt, false);
            return false;
        }

        await release(ids, claimedAt, true);
        this.backOff(status === 429 ? retryAfterMs(retryAfter, this.now()) : 0);
        return false;
    }

    private backOff(minimumMs: number): void {
        if (this.stopped) {
            return;
        }

        this.failures++;
        const ceiling = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** (this.failures - 1));
        const jitter = Math.floor((this.deps.random ?? Math.random)() * ceiling);
        const delay = Math.max(minimumMs, jitter);

        this.nextAttemptAt = this.now() + delay;

        if (this.retryTimer !== undefined) {
            clearTimeout(this.retryTimer);
        }

        this.retryTimer = setTimeout(() => {
            this.retryTimer = undefined;
            this.requestDrain('retry').then();
        }, delay);
    }

    // No response, so nothing is deleted here.
    beacon(): boolean {
        if (!telemetryEnabled() || this.halted) {
            return false;
        }

        // pagehide also fires on every back-forward cache freeze.
        const unsent = beaconRows().filter((row) => !this.beaconed.has(row.uuid));
        const rows = selectWithin(unsent, MAX_BATCH_RECORDS, MAX_BATCH_BYTES);
        const send = this.deps.sendBeacon
            ?? (globalThis.navigator?.sendBeacon
                ? (url: string, data: Blob) => globalThis.navigator.sendBeacon(url, data)
                : undefined);

        if (rows.length === 0 || !send) {
            return false;
        }

        try {
            const body = new Blob([envelope(rows)], { type: 'application/json' });
            const sent = send(TELEMETRY_URL, body);

            if (sent) {
                rows.forEach((row) => this.beaconed.add(row.uuid));
            }

            return sent;
        }
        catch {
            return false;
        }
    }
}

export const telemetrySync = new TelemetrySync();
